import { create } from 'zustand'
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from 'zustand/middleware'

import type { InstructionCheck } from './types'

const STORAGE_NAME = 'counselpro.pending-fixes'

export interface PendingFix {
  note: string
  triage: InstructionCheck
  checkedAt: string
}

interface PendingFixActions {
  savePendingFix: (
    projectSlug: string,
    segmentId: number,
    note: string,
    triage: InstructionCheck,
  ) => void
  clearPendingFix: (projectSlug: string, segmentId: number) => void
}

interface PendingFixStore {
  fixes: Record<string, PendingFix>
  actions: PendingFixActions
}

const pendingFixKey = (projectSlug: string, segmentId: number) =>
  `${projectSlug}:${segmentId}`

// Every call throws in a private window or with site data blocked. A fix we
// cannot store is worth less than the page it would take down.
const forgivingLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name)
    } catch {
      return null
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value)
    } catch {
      // Nowhere to put it, so the fix lives only as long as the panel does.
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name)
    } catch {
      // Nothing was ever written, so nothing is left behind.
    }
  },
}

// A fix the user already paid to check, kept per cycle so paging away or
// reloading does not ask them to type and check it again.
export const usePendingFixStore = create<PendingFixStore>()(
  persist(
    (set) => ({
      fixes: {},

      actions: {
        savePendingFix: (projectSlug, segmentId, note, triage) =>
          set((state) => ({
            fixes: {
              ...state.fixes,
              [pendingFixKey(projectSlug, segmentId)]: {
                note,
                triage,
                checkedAt: new Date().toISOString(),
              },
            },
          })),

        clearPendingFix: (projectSlug, segmentId) =>
          set((state) => {
            const key = pendingFixKey(projectSlug, segmentId)
            if (!state.fixes[key]) return state
            const fixes = { ...state.fixes }
            delete fixes[key]
            return { fixes }
          }),
      },
    }),
    {
      name: STORAGE_NAME,
      storage: createJSONStorage(() => forgivingLocalStorage),
      partialize: (state) => ({ fixes: state.fixes }),
    },
  ),
)

export const usePendingFix = (
  projectSlug: string | undefined,
  segmentId: number,
): PendingFix | null =>
  usePendingFixStore((state) =>
    projectSlug
      ? (state.fixes[pendingFixKey(projectSlug, segmentId)] ?? null)
      : null,
  )

export const pendingFixActions = () => usePendingFixStore.getState().actions
